import { Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { BaseService } from '../../abstract/base.service';
import { Certification } from '../data-models/certification.model';
import { CertificationCreateRequest } from '../request-models/certification-create-request.model';
import { CertificationUpdateRequest } from '../request-models/certification-update-request.model';
import { isRecord } from '../utils/is-record';
import { nullableString } from '../utils/wire-value-utils';
import { toCertificationCreateRequest, toCertificationUpdateRequest } from '../utils/certification-request-utils';

/**
 * Organization and member certifications: `/admin/certifications` (hub `admin-certifications-controller.ts`).
 * Failures arrive as `ApiError`.
 */
@Injectable({
  providedIn: 'root'
})
export class CertificationService extends BaseService<Certification> {
  constructor() {
    super('admin/certifications', Certification);
  }

  /** GET /admin/certifications?organizationId=X&ownerType=organization */
  getOrganizationCertifications(organizationId: string): Observable<Certification[]> {
    return this.getMany({ query: { organizationId, ownerType: 'organization' } });
  }

  /** GET /admin/certifications?profileId=X&ownerType=profile */
  getMemberCertifications(profileId: string): Observable<Certification[]> {
    return this.getMany({ query: { profileId, ownerType: 'profile' } });
  }

  /** GET /admin/certifications/:id. Errors when the hub returns no certification. */
  getCertification(id: string): Observable<Certification> {
    return this.get(id).pipe(map(cert => required(cert, 'Certification not found')));
  }

  /** POST /admin/certifications for an organization. */
  uploadOrganizationCertification(organizationId: string, data: CertificationCreateRequest): Observable<Certification> {
    const body = toCertificationCreateRequest({ ...data, ownerType: 'organization', organizationId });
    return this.create(body).pipe(map(cert => required(cert, 'Failed to create certification')));
  }

  /** POST /admin/certifications for a member. */
  uploadMemberCertification(profileId: string, data: CertificationCreateRequest): Observable<Certification> {
    const body = toCertificationCreateRequest({ ...data, ownerType: 'profile', profileId });
    return this.create(body).pipe(map(cert => required(cert, 'Failed to create certification')));
  }

  /** PUT /admin/certifications/:id with only the writable fields that are set. */
  updateCertification(id: string, data: CertificationUpdateRequest): Observable<Certification> {
    return this.update(id, toCertificationUpdateRequest(data)).pipe(map(cert => required(cert, 'Failed to update certification')));
  }

  /** GET /admin/certifications/:id/download-url: a short-lived signed URL for the document. */
  getDownloadUrl(id: string): Observable<string> {
    const read = (data: unknown) => (isRecord(data) ? nullableString(data['downloadUrl']) ?? null : null);
    return this.request('GET', { id, path: 'download-url' }, read).pipe(map(url => required(url, 'Could not open this document')));
  }

  /** DELETE /admin/certifications/:id */
  deleteCertification(id: string): Observable<void> {
    return this.delete(id);
  }
}

function required<T>(value: T | null, message: string): T {
  if (value === null) {
    throw new Error(message);
  }
  return value;
}
